import * as THREE from "three";
import {
  createDeliveryScene,
  createExplosionScene,
  createOpeningScene,
  STORY_POINTS
} from "./rc-3d-scenes.js";
import { heroProgress, lerp, smoothstep } from "./rc-3d-scroll.js";

const canvas = document.getElementById("rc-hero-canvas");
const hero = document.getElementById("hero");
const logo = document.querySelector(".rc-logo");
const intro = document.querySelector('[data-copy="intro"]');
const destinations = document.querySelector(".rc-destinations");
const chapter = document.querySelector('.rc-chapter');
const progressBar = document.querySelector('.rc-progress-fill');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const renderer = new THREE.WebGLRenderer({
  canvas,
  alpha: true,
  antialias: true,
  powerPreference: "high-performance"
});
renderer.setClearColor(0x000000, 0);
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.15;

const scene = new THREE.Scene();
scene.fog = new THREE.Fog(0x252b2e, 22, 55);
// Reflection cards produce long studio highlights, without baked-in glare.
const studio = new THREE.Scene();
studio.background = new THREE.Color(0x343b42);
for (const [position, dimensions, intensity] of [
  [[-4, 5, 3], [2, 9], 5], [[6, 3, -2], [1.5, 10], 4],
  [[0, 7, 0], [9, 3], 3], [[0, 1, -7], [5, 7], 1.5]
]) {
  const card = new THREE.Mesh(new THREE.PlaneGeometry(...dimensions),
    new THREE.MeshBasicMaterial({ color: new THREE.Color().setScalar(intensity), side: THREE.DoubleSide }));
  card.position.set(...position);
  card.lookAt(0, 0, 0);
  studio.add(card);
}
const pmrem = new THREE.PMREMGenerator(renderer);
const environment = pmrem.fromScene(studio, 0.06, 0.1, 100);
scene.environment = environment.texture;
scene.environmentIntensity = 0.85;
studio.traverse(object => { object.geometry?.dispose(); object.material?.dispose(); });
pmrem.dispose();

const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
const target = new THREE.Vector3();
scene.add(camera);

const inspectionLight = new THREE.PointLight(0xffffff, 0, 9);
inspectionLight.position.set(0.8, 1.1, -0.6);
camera.add(inspectionLight);

const hemi = new THREE.HemisphereLight(0xeaf0f6, 0x25221e, 0.7);
scene.add(hemi);

const key = new THREE.DirectionalLight(0xfff5e7, 2.6);
key.position.set(3.8, 5.4, 4.8);
key.castShadow = true;
key.shadow.mapSize.set(2048, 2048);
key.shadow.camera.left = key.shadow.camera.bottom = -12;
key.shadow.camera.right = key.shadow.camera.top = 12;
key.shadow.normalBias = 0.025;
scene.add(key);

const ribbon = new THREE.PointLight(0xd7e2df, 2.4, 10);
ribbon.position.set(-3.8, 2.2, 2.5);
scene.add(ribbon);

const opening = createOpeningScene();
scene.add(opening.root);
const delivery = createDeliveryScene();
scene.add(delivery.root);
const explosion = createExplosionScene();
scene.add(explosion.root);

let previousTime = performance.now();
let elapsed = 0;
let animationFrame;
let progress = 0;
const SCENE_THREE_START = 0.56;
const SCENE_FOUR_START = 0.7;
const EXPLOSION_START = 0.82;

function resize() {
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.75);
  renderer.setPixelRatio(pixelRatio);
  renderer.setSize(width, height, false);
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
}

function updateCamera(p, sceneThree, sceneFour) {
  const earlierProgress = Math.min(1, p / SCENE_THREE_START);
  const pulleyCenter = STORY_POINTS.pulleyCenter;
  const sceneOne = Math.min(1, earlierProgress / 0.5);
  const elevate = smoothstep(0.22, 0.58, sceneOne);
  const orbit = smoothstep(0.62, 0.96, sceneOne);
  const elevation = elevate * Math.PI * 0.25;
  const azimuth = orbit * Math.PI * 0.25;
  const cameraMove = Math.max(elevate, orbit);
  const radius = lerp(9.07, 5.9, cameraMove);
  const sceneTwo = smoothstep(0.5, 1, earlierProgress);
  const follow = smoothstep(0.08, 0.62, sceneTwo);
  const inspect = smoothstep(0.58, 1, sceneTwo);

  const baseTarget = pulleyCenter
    .clone()
    .addScaledVector(STORY_POINTS.handoffDirection, follow * 4.35)
    .lerp(STORY_POINTS.mandrelEndCenter, inspect * 0.78);
  if (camera.aspect < 0.8) {
    const mobileLead = THREE.MathUtils.lerp(
      1.35,
      0,
      THREE.MathUtils.smoothstep(camera.aspect, 0.46, 0.8)
    );
    baseTarget.addScaledVector(
      STORY_POINTS.handoffDirection,
      mobileLead * sceneTwo
    );
  }
  const viewDirection = new THREE.Vector3(
    Math.sin(azimuth) * Math.cos(elevation),
    Math.sin(elevation),
    Math.cos(azimuth) * Math.cos(elevation)
  );
  const mobilePullback = camera.aspect < 0.8
    ? THREE.MathUtils.lerp(
      2.05,
      1,
      THREE.MathUtils.smoothstep(camera.aspect, 0.46, 0.8)
    )
    : 1;
  const cameraDistance = lerp(radius, 6.1, sceneTwo) *
    lerp(1, mobilePullback, sceneTwo);

  camera.position.copy(baseTarget).addScaledVector(viewDirection, cameraDistance);
  target.copy(baseTarget);
  camera.up.set(0, 1, 0);

  if (sceneThree > 0.001) {
    const axis = STORY_POINTS.mandrelAxis;
    const anchor = STORY_POINTS.mandrelCenter.clone();
    const cameraOffset = camera.position.clone().sub(anchor);
    const targetOffset = target.clone().sub(anchor);
    const orbit = new THREE.Quaternion().setFromAxisAngle(
      axis,
      delivery.sceneThreeMotion.rotation
    );
    const axialShift = axis.clone().multiplyScalar(delivery.sceneThreeMotion.axial);
    camera.position.copy(anchor).add(cameraOffset.applyQuaternion(orbit)).add(axialShift);
    target.copy(anchor).add(targetOffset.applyQuaternion(orbit)).add(axialShift);
    camera.up.lerp(axis, smoothstep(0, 0.8, sceneThree)).normalize();
  }

  const settle = smoothstep(0.70, 0.84, p);
  const productTarget = STORY_POINTS.mandrelCenter;
  const productView = new THREE.Vector3(0.5, Math.SQRT1_2, 0.5).normalize();
  const distance = 15.5 * Math.max(1, 0.72 / camera.aspect);
  const offset = camera.position.clone().sub(productTarget);
  const axis = STORY_POINTS.mandrelAxis;
  const axial = offset.dot(axis);
  const radial = offset.clone().addScaledVector(axis, -axial);
  const productRadius = radial.length();
  radial.normalize();
  const orbitAngle = Math.atan2(new THREE.Vector3().crossVectors(radial, productView).dot(axis), radial.dot(productView));
  // Orbit around the tube rather than cutting through it or passing over a pole.
  radial.applyAxisAngle(axis, orbitAngle * settle);
  camera.position.copy(productTarget).addScaledVector(radial, lerp(productRadius, distance, settle))
    .addScaledVector(axis, axial * (1 - settle));
  target.lerp(productTarget, settle);
  camera.up.lerp(STORY_POINTS.mandrelAxis, settle).normalize();
  camera.lookAt(target);
}

function updateDom(p, explosionProgress) {
  logo.style.opacity = '0.94';
  intro.classList.toggle("is-hidden", p > 0.12);
  const revealed = explosionProgress > 0.72 || reducedMotion.matches;
  destinations.classList.toggle("is-visible", revealed);
  destinations.inert = !revealed;
  const stage = progress < 0.28 ? 0 : progress < 0.56 ? 1 : progress < 0.7 ? 2 : progress < 0.82 ? 3 : 4;
  chapter.textContent = ['01 / Carbon tow', '02 / Guided under tension', '03 / Precision winding', '04 / Engineered in layers', '05 / Find your tube'][stage];
  progressBar.style.transform = `scaleX(${progress})`;
  document.querySelectorAll('[data-stage]').forEach((button, index) => button.setAttribute('aria-current', index === stage ? 'step' : 'false'));
}

function tick(now = performance.now()) {
  const dt = Math.min((now - previousTime) / 1000, 0.05);
  previousTime = now;
  const requested = heroProgress(hero);
  progress += (requested - progress) * (reducedMotion.matches ? 1 : 1 - Math.exp(-dt * 5.5));
  if (Math.abs(requested - progress) < 0.00001) progress = requested;
  if (!reducedMotion.matches) elapsed += dt;
  const earlierProgress = Math.min(1, progress / SCENE_THREE_START);
  const sceneThree = smoothstep(SCENE_THREE_START, SCENE_FOUR_START, progress);
  const sceneFour = smoothstep(SCENE_FOUR_START, EXPLOSION_START, progress);
  const explosionProgress = smoothstep(EXPLOSION_START, 1, progress);
  inspectionLight.intensity = smoothstep(0.04, 0.3, sceneThree) * 0.7;
  opening.update(Math.min(1, earlierProgress / 0.5), elapsed);
  opening.root.visible = sceneThree < 0.01;
  delivery.update(
    smoothstep(0.5, 1, earlierProgress),
    elapsed,
    sceneThree,
    explosionProgress
  );
  explosion.update(explosionProgress, delivery.sceneThreeMotion);
  updateCamera(progress, sceneThree, sceneFour);
  updateDom(earlierProgress, explosionProgress);

  renderer.render(scene, camera);
  animationFrame = requestAnimationFrame(tick);
}

window.addEventListener('resize', resize, { passive: true });
document.addEventListener('visibilitychange', () => {
  cancelAnimationFrame(animationFrame);
  if (!document.hidden) { previousTime = performance.now(); tick(); }
});
document.querySelectorAll('[data-stage]').forEach(button => button.addEventListener('click', () => {
  const travel = hero.offsetHeight - window.innerHeight;
  window.scrollTo({ top: hero.offsetTop + Number(button.dataset.stage) * travel, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
}));
document.querySelector('.rc-replay').addEventListener('click', () => window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'instant' : 'smooth' }));
canvas.addEventListener('webglcontextlost', event => {
  event.preventDefault();
  cancelAnimationFrame(animationFrame);
  document.body.classList.add('render-unavailable');
  destinations.inert = false;
});
canvas.addEventListener('webglcontextrestored', () => window.location.reload());
resize();
tick();
